import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import {
  Car,
  Users,
  FileText,
  DollarSign,
  Wrench,
  TrendingUp,
  CheckCircle2,
  Clock,
  CreditCard,
} from "lucide-react";

function KpiCard({ icon: Icon, label, value, hint, accent = "kpi-gold" }) {
  return (
    <div className={`surface kpi-accent ${accent} p-5 rounded-md`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-5 h-5" style={{ color: "var(--accent-color)" }} />
      </div>
      <div className="font-heading text-4xl mt-3" style={{ color: "var(--accent-color)" }}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [r, setR] = useState(null);
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/reports/summary").then((res) => setR(res.data));
    api.get("/payments/transactions").then((res) => setTxns(res.data || [])).catch(() => {});
  }, []);

  return (
    <AdminShell title="Operations overview">
      {!r ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={DollarSign} label="Gross Revenue" value={`$${(r.gross_revenue || 0).toLocaleString()}`} hint="All deals" accent="kpi-gold" />
            <KpiCard icon={Car} label="Vehicles Sold" value={r.vehicles_sold} hint={`${r.vehicles_total} total in stock`} accent="kpi-red" />
            <KpiCard icon={TrendingUp} label="Finance Pen." value={`${r.finance_penetration}%`} hint="Approved / deals" accent="kpi-navy" />
            <KpiCard icon={Wrench} label="Service Bookings" value={r.service_appointments} hint="All-time" accent="kpi-chrome" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={CheckCircle2} label="Available" value={r.vehicles_available} accent="kpi-emerald" />
            <KpiCard icon={Clock} label="Pending" value={r.vehicles_pending} accent="kpi-amber" />
            <KpiCard icon={Users} label="Open Leads" value={r.leads_open} hint={`${r.leads_total} total`} accent="kpi-navy" />
            <KpiCard icon={FileText} label="Signed Deals" value={r.deals_signed} hint={`${r.deals_total} in pipeline`} accent="kpi-gold" />
          </div>

          <div className="surface rounded-sm p-6">
            <div className="label-eyebrow mb-3">Welcome to Wakzy DealerOS</div>
            <p className="text-slate-300 max-w-2xl">
              Use the sidebar to manage inventory, work the CRM pipeline, review
              finance applications, schedule service, and pull reports. All
              customer-facing kiosk activity flows directly into these queues in real
              time.
            </p>
          </div>

          <div className="surface rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-4 h-4" style={{ color: "var(--wakzy-gold)" }} />
              <span className="label-eyebrow">Recent reservation deposits</span>
            </div>
            {txns.length === 0 ? (
              <div className="text-sm text-slate-500">No deposits yet. Customers can reserve a vehicle from the kiosk with a $500 hold.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="py-2">Date</th>
                    <th>Vehicle</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.slice(0, 6).map((t) => (
                    <tr key={t.id} className="border-t border-white/5" data-testid={`txn-row-${t.id}`}>
                      <td className="py-2 text-slate-400 text-xs">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2">{t.metadata?.vehicle_label || t.vehicle_id?.slice(0, 8)}</td>
                      <td className="py-2">${(t.amount_total / 100).toFixed(2)} {t.currency?.toUpperCase()}</td>
                      <td className="py-2">
                        <span className={`pill ${t.payment_status === "paid" ? "pill-available" : "pill-pending"}`}>{t.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
